import { useMemo, useState } from 'react';
import { trackEvent } from '@kcd/event-tracker';

import { getResizeImage } from '~/components/Image';
import {
  useCouponDownloadAllMutation,
  useDownloadedCouponsQuery,
} from '~/domains/coupons/queries';
import { checkIsCouponDownloadable } from '~/domains/coupons/utils';
import { getOAIErrorMessage } from '~/libs/openapi';
import { OAIApiError } from '~/libs/openApiError';
import { appAlert, openDialogAsync } from '~/utils/mobileApp';

import EventFloatingWrapper from '../../components/EventFloatingWrapper';

type Props = {
  mainImage?: {
    default?: string;
    disabled?: string;
  };
  ctaText?: string;
  couponIds: number[];
};

export function MainImageSection({
  mainImage = {},
  ctaText,
  couponIds,
}: Props) {
  const [disabledCoupon, setDisabledCoupon] = useState(false);
  const downloadAllCoupon = useCouponDownloadAllMutation();

  const { data: downloadedCoupons = [] } = useDownloadedCouponsQuery(couponIds);

  const hasDownloadableCoupon = downloadedCoupons.some(
    checkIsCouponDownloadable,
  );

  const disabled = useMemo(() => {
    return disabledCoupon || !hasDownloadableCoupon;
  }, [disabledCoupon, hasDownloadableCoupon]);

  const onClickCouponDownload = async () => {
    if (!hasDownloadableCoupon || disabledCoupon || !couponIds.length) return;

    try {
      // 쿠폰 다운로드버튼을 빠르게 여러번 누르는걸 방지하기위함
      setDisabledCoupon(true);

      await downloadAllCoupon({
        requestBody: {
          couponIds,
        },
      });

      trackEvent('downloaded coupon in 마켓/이벤트/최상단', {
        couponName: couponIds,
      });

      void openDialogAsync({
        type: 'alert',
        title: '쿠폰이 발급되었습니다.',
        description: '[마이페이지 > 할인쿠폰] 에서 발급된 쿠폰을 확인하세요.',
      });
    } catch (err) {
      if (err instanceof OAIApiError) {
        appAlert({
          title: '쿠폰 발급에 실패했습니다.',
          description: getOAIErrorMessage(err),
        });

        setDisabledCoupon(false);
      }

      throw err;
    }
  };

  return (
    <>
      <button type="button" onClick={() => onClickCouponDownload()}>
        <img
          src={getResizeImage({
            src: disabled
              ? mainImage?.disabled || mainImage?.default || ''
              : mainImage?.default || '',
            width: 640,
          })}
          alt="이벤트 배너"
        />
      </button>
      <EventFloatingWrapper
        ctaText={ctaText}
        disabled={false}
        onClickCouponDownload={onClickCouponDownload}
      />
    </>
  );
}
